import React from 'react';
import { formatFileSize, type BaseUploadFileItem } from './file';
import { cn } from '../../../cn';
import { DownloadIcon, File, Trash2 } from 'lucide-react';

type FileItemProps = {
  file: BaseUploadFileItem;
  disabled?: boolean;
  onRemoveFile?: () => void;
  onDownloadFile?: () => void;
};

export function FileItem({
  file,
  disabled,
  onRemoveFile,
  onDownloadFile,
}: FileItemProps) {
  const hasError = !!file.error;

  return (
    <React.Fragment>
      <li
        className={cn(
          'flex items-center gap-3 rounded-md border p-4',
          hasError
            ? 'border-destructive bg-destructive/5'
            : 'border-border bg-muted/30',
        )}
      >
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-md',
            hasError ? 'bg-destructive/10' : 'bg-muted',
          )}
        >
          <File className={cn('h-5 w-5', 'text-muted-foreground')} />
        </div>

        <div className="min-w-0 flex-1">
          <p className={cn('truncate text-sm font-medium', 'text-foreground')}>
            {file.fileName}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatFileSize(file.fileSize)}
          </p>
        </div>

        <button
          onClick={onDownloadFile}
          className={cn(
            'shrink-0 rounded-md p-1 transition-colors focus:outline-none focus:ring-2 focus:ring-ring hover:bg-muted hover:text-foreground',
          )}
          type="button"
        >
          <DownloadIcon size={16} />
        </button>

        {!disabled ? (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onRemoveFile?.();
            }}
            className={cn(
              'shrink-0 rounded-md p-1 transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
              hasError
                ? 'text-destructive hover:bg-destructive/10'
                : 'hover:bg-muted hover:text-foreground',
            )}
            aria-label={`Remove ${file.fileName}`}
          >
            <Trash2 size={16} />
          </button>
        ) : null}
      </li>
      {hasError ? (
        <div className="text-xs text-destructive mt-1">{file.error}</div>
      ) : null}
    </React.Fragment>
  );
}
